// ─── UUIDs ───────────────────────────────────────────────────────────────────

const SERVICE_UUID = '0000a001-0000-1000-8000-00805f9b34fb'
const CHAR_UUID = '0000c300-0000-1000-8000-00805f9b34fb'

// ─── Protocol constants ───────────────────────────────────────────────────────

const CMD_BLUETOOTH = 0x08
const CMD_DEBUG_ERROR = 0xfe

// How long to wait for a reply before giving up on a command.
const COMMAND_TIMEOUT = 10000

// Quanto si aspetta un dispositivo BLE prima di dichiarare che non c'è.
// Configurabile con BLE_SCAN_TIMEOUT (vedi nuxt.config.js).
const SCAN_TIMEOUT = Number(process.env.bleScanTimeout) || 1000

// Con autoSelect, quanto si aspetta dopo il primo dispositivo trovato prima di
// sceglierlo da soli: se nel frattempo ne compare un altro (es. due studi
// vicini) la scelta torna all'utente.
const AUTO_SELECT_DELAY = 500

// Errore riconoscibile: "nessun dispositivo BLE", non un guasto.
const noDeviceError = () => {
  const err = new Error('No BLE device found')
  err.code = 'NO_DEVICE'
  return err
}

// "ciao sono il pic" — device sends this as a wake-up before it's ready
const HELLO = [
  0x63, 0x69, 0x61, 0x6f, 0x20, 0x73, 0x6f, 0x6e, 0x6f, 0x20, 0x69, 0x6c, 0x20,
  0x70, 0x69, 0x63, 0x0d, 0x0a,
]

export const FUNC = {
  recognition: 0x0000,
  info: 0x0001,
  battery: 0x0002,
  setData: 0x0004,
  getData: 0x0005,
  totalMeasureSend: 0x0006,
  regionalMeasureSend: 0x0007,
  dataReceivedFeedback: 0x0008,
  deviceAutoOff: 0x0009,
}

export const MEASURE_TYPE = {
  bodyRight: 0x00,
  armRight: 0x01,
  legRight: 0x02,
  bodyLeft: 0x03,
  armLeft: 0x04,
  legLeft: 0x05,
  bodyUp: 0x06,
  bodyLow: 0x07,
  trunkRight: 0x08,
  trunkLeft: 0x09,
}

// Segment names used by the regional exam, in device order — index matches the
// MEASURE_TYPE value.
const BODY_PARTS = [
  'rightBody',
  'armRight',
  'legRight',
  'leftBody',
  'armLeft',
  'legLeft',
  'upperBody',
  'lowerBody',
  'trunkRight',
  'trunkLeft',
]

const DEBUG_ERRORS = [
  'crc',
  'deviceId',
  'functionRequest',
  'recognitionNotDone',
  'packetLength',
  'data',
]

// ─── Encoding helpers ────────────────────────

// Sets bit 7; ensures the byte is never 0x00 on the wire.
const encode8 = (b) => (b & 0xff) | 0x80
const decode8 = (b) => b & 0x7f

// Encodes a 15-bit value into a 16-bit word where both bytes have bit 7 set.
const encode16 = (v) => ((v << 1) & 0xff00) | (v & 0x00ff) | 0x8080
const decode16 = (v) => ((v >> 1) & 0x3f80) | (v & 0x007f)

const u16 = (hi, lo) => ((hi << 8) | lo) & 0xffff
const u16Bytes = (v) => [(v >> 8) & 0xff, v & 0xff]

const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms))

// Date/time payload (mirrors AkernDateTime.serialize).
// The year is encode16'd first; since that already sets bit 7 on both bytes,
// buildPacket's encode8 is a no-op for them.
export const dateBytes = (date) => [
  date.getHours(),
  date.getMinutes(),
  date.getSeconds(),
  date.getDate(),
  date.getMonth() + 1,
  ...u16Bytes(encode16(date.getFullYear())),
]

// CRC-CCITT variant used by the device firmware.
function computeCrc(bytes) {
  let crc = 0x0000
  for (const b of bytes) {
    crc = ((crc >> 8) | (crc << 8)) & 0xffff
    crc ^= b
    crc ^= (crc & 0xff) >> 4
    crc ^= (crc << 12) & 0xffff
    crc ^= ((crc & 0xff) << 5) & 0xffff
  }
  // Ensure neither byte is 0x00 on the wire.
  if ((crc & 0xff00) === 0 || (crc & 0x00ff) === 0)
    crc = (crc | 0x8080) & 0xffff
  return crc
}

// ─── Packet building ──────────────────────────────────────────────────────────

// Packet layout:
//   [cmd(1), funcHi(1), funcLo(1), encode8(data[0..N])(N), lenHi(1), lenLo(1), crcHi(1), crcLo(1), 0x00(1)]
//
// length field = 3 + rawData.length  (byte offset where the length field starts)
export function buildPacket(funcValue, rawData = []) {
  const funcEncoded = encode16(funcValue)
  const encodedData = rawData.map(encode8)
  const length = 3 + rawData.length
  const lenEncoded = encode16(length)

  const request = [
    CMD_BLUETOOTH,
    ...u16Bytes(funcEncoded),
    ...encodedData,
    ...u16Bytes(lenEncoded),
  ]
  const crc = computeCrc(request)
  return new Uint8Array([...request, ...u16Bytes(crc), 0x00])
}

// ─── Packet parsing ───────────────────────────────────────────────────────────

function parsePacket(bytes) {
  if (bytes.length === HELLO.length && bytes.every((b, i) => b === HELLO[i])) {
    return { isHello: true }
  }

  const commandType = bytes[0]
  if (commandType === CMD_DEBUG_ERROR) {
    const code = bytes[1]
    const err = new Error(
      `BLE device error: ${DEBUG_ERRORS[code] ?? 'unknown'} (0x${code.toString(
        16
      )})`
    )
    // The debugger shows the raw reply, error packets included.
    err.bytes = bytes
    throw err
  }

  const functionValue = decode16(u16(bytes[1], bytes[2]))
  // length field sits 5 bytes from the end: [...data, lenHi, lenLo, crcHi, crcLo, 0x00]
  const index = bytes.length - 5
  const length = decode16(u16(bytes[index], bytes[index + 1]))
  const raw = Array.from(bytes.slice(3, length)).map(decode8)

  return { functionValue, raw }
}

// ─── Response data parsers ────────────────────────────────────────────────────

const RECOGNITION_VALUES = ['ok', 'notOk', 'greenCard']
const MEASURE_STATUS = ['ok', 'notReady', 'wrongScreen']
const MEASURE_TYPE_BY_VALUE = Object.fromEntries(
  Object.entries(MEASURE_TYPE).map(([k, v]) => [v, k])
)

function parseData(functionValue, raw) {
  switch (functionValue) {
    case FUNC.recognition:
      return { recognition: RECOGNITION_VALUES[raw[0]] ?? 'unknown' }

    case FUNC.battery:
      return raw[0]

    case FUNC.info:
      return {
        hwMajorVersion: raw[0],
        hwMinorVersion: raw[1],
        fwMajorVersion: raw[2],
        fwMinorVersion: raw[3],
      }

    case FUNC.setData:
      return { ok: raw[0] === 0x00 }

    case FUNC.getData: {
      const year = decode16(u16(raw[5], raw[6]))
      return new Date(year, raw[4] - 1, raw[3], raw[0], raw[1], raw[2])
    }

    case FUNC.totalMeasureSend:
    case FUNC.regionalMeasureSend:
      return {
        status: MEASURE_STATUS[raw[0]] ?? 'unknown',
        type: MEASURE_TYPE_BY_VALUE[raw[1]] ?? 'unknown',
        resistance: decode16(u16(raw[2], raw[3])) / 10, // Rx Ω
        reactance: decode16(u16(raw[4], raw[5])) / 10, // Xc Ω
        phaseAngle: decode16(u16(raw[6], raw[7])) / 10, // Pha °
        quality: raw[8],
      }

    case FUNC.deviceAutoOff:
      return { ok: raw[0] === 0x00 }

    default:
      return { raw }
  }
}

// ─── Non-reactive BLE handles (intentionally outside Vuex state) ──────────────

let _btDevice = null
let _server = null
let _characteristic = null
// Vero mentre una requestDevice() è pendente: serve a disconnect() per sapere
// che c'è un picker da annullare anche se non c'è ancora nessun handle.
let _scanning = false
// Handler di gattserverdisconnected attualmente registrato, tenuto per poterlo
// rimuovere: l'istanza BluetoothDevice sopravvive alla disconnessione.
let _onDisconnected = null

const forgetDisconnectListener = () => {
  if (_btDevice && _onDisconnected)
    _btDevice.removeEventListener('gattserverdisconnected', _onDisconnected)
  _onDisconnected = null
}

// ─── Platform helper ──────────────────────────────────────────────────────────

function getIpc() {
  return (
    (typeof window !== 'undefined' &&
      (window.ipcRenderer || (window.parent && window.parent.ipcRenderer))) ||
    null
  )
}

// ─── Low-level write → await notification ────────────────────────────────────

// Subscribes to the next characteristicvaluechanged event BEFORE writing so the
// notification is never missed. Mirrors writeAkernCharacteristic in the Dart SDK.
function writeAndAwaitNotification(characteristic, packet) {
  return new Promise((resolve, reject) => {
    const cleanup = () => {
      clearTimeout(timer)
      characteristic.removeEventListener('characteristicvaluechanged', handler)
    }

    function handler(event) {
      cleanup()
      resolve(new Uint8Array(event.target.value.buffer))
    }

    characteristic.addEventListener('characteristicvaluechanged', handler)

    const timer = setTimeout(() => {
      cleanup()
      reject(new Error('Device did not respond'))
    }, COMMAND_TIMEOUT)

    characteristic.writeValueWithResponse(packet).catch((err) => {
      cleanup()
      reject(err)
    })
  })
}

// Writes a packet and returns the decoded { functionValue, raw } response.
// If the device responds with the hello sequence it's not ready yet — retry.
//
// Assumes one command in flight at a time: every flow below awaits each step,
// and the UI drives one flow at a time.
async function sendPacket(packet) {
  if (!_characteristic) throw new Error('Device not connected')

  const bytes = await writeAndAwaitNotification(_characteristic, packet)
  const parsed = parsePacket(bytes)

  if (parsed.isHello) return sendPacket(packet)

  return { ...parsed, bytes }
}

// Fire-and-forget write. Acknowledgements get no reply from the device, so
// waiting for a notification would only time out — the serial gateway writes
// them the same way, without reading a response.
async function sendPacketNoReply(packet) {
  if (!_characteristic) throw new Error('Device not connected')

  await _characteristic.writeValueWithResponse(packet)
}

// Builds packet, writes, awaits notification, applies the response parser.
// parse === false means the device sends nothing back.
async function sendCommand(funcValue, rawData = [], parse = true) {
  const packet = buildPacket(funcValue, rawData)

  if (!parse) {
    await sendPacketNoReply(packet)
    return null
  }

  const parsed = await sendPacket(packet)
  return parseData(parsed.functionValue, parsed.raw)
}

// ─── Vuex store ───────────────────────────────────────────────────────────────

const state = () => ({
  connected: false,
  device: null, // { id, name }
  foundDevices: [], // [{ deviceId, deviceName }] — populated on Electron only
  measurement: null, // { status, type, resistance, reactance, phaseAngle, quality }
  battery: null, // number (raw device value)
  info: null, // { hwMajorVersion, hwMinorVersion, fwMajorVersion, fwMinorVersion }
  error: null,
})

const getters = {
  connected: (s) => s.connected,
  device: (s) => s.device,
  foundDevices: (s) => s.foundDevices,
  measurement: (s) => s.measurement,
  battery: (s) => s.battery,
  info: (s) => s.info,
  error: (s) => s.error,
}

const mutations = {
  setConnected(state, v) {
    state.connected = v
  },
  setDevice(state, v) {
    state.device = v
  },
  setFoundDevices(state, v) {
    state.foundDevices = v
  },
  setMeasurement(state, v) {
    state.measurement = v
  },
  setBattery(state, v) {
    state.battery = v
  },
  setInfo(state, v) {
    state.info = v
  },
  setError(state, v) {
    state.error = v
  },
  reset(state) {
    state.connected = false
    state.device = null
    state.foundDevices = []
    state.measurement = null
    state.battery = null
    state.info = null
    state.error = null
  },
}

const actions = {
  // Triggers the OS / browser device picker.
  // On Electron: the main process intercepts select-bluetooth-device, scans,
  // and emits ble:devicesFound. The UI calls selectDevice() to complete the pick.
  // On Web: the browser shows its own native picker — no extra steps needed.
  // Rifiuta con err.code === 'NO_DEVICE' se entro il timeout non compare nulla,
  // così il chiamante può ripiegare sul seriale senza confondere questo caso con
  // un errore di connessione vero. timeoutMs = 0 disattiva il countdown: si
  // aspetta finché l'utente non sceglie o chiude (debugger BLE).
  // autoSelect (solo Electron): se dopo AUTO_SELECT_DELAY c'è un solo
  // dispositivo lo si sceglie senza passare dall'utente; con più dispositivi la
  // scelta resta alla UI tramite foundDevices.
  async scan(
    { commit },
    { timeoutMs = SCAN_TIMEOUT, autoSelect = false } = {}
  ) {
    commit('setError', null)
    commit('setFoundDevices', [])

    const ipc = getIpc()
    let timer
    let autoSelectTimer
    let timedOut = false

    // Chromium richiama il gestore a ogni advertisement, cioè decine di volte al
    // secondo: entrambi i rami qui sotto devono essere a prova di ripetizione.
    let deviceFound = false
    let lastKey = ''
    let lastDevices = []

    if (ipc) {
      ipc.on('ble:devicesFound', (_event, devices) => {
        lastDevices = devices

        // Si committa solo quando la lista cambia davvero, altrimenti sono
        // decine di mutazioni Vuex (e altrettanti re-render) al secondo.
        const key = devices.map((device) => device.deviceId).join('|')
        if (key !== lastKey) {
          lastKey = key
          commit('setFoundDevices', devices)
        }

        // Il countdown si ferma una volta sola e non riparte: nessuno rimette
        // mai in armo il timer.
        if (!deviceFound && devices.length > 0) {
          deviceFound = true
          clearTimeout(timer)

          if (autoSelect) {
            autoSelectTimer = setTimeout(() => {
              if (lastDevices.length === 1)
                ipc.send('ble:selectDevice', {
                  deviceId: lastDevices[0].deviceId,
                })
            }, AUTO_SELECT_DELAY)
          }
        }
      })
    }

    // requestDevice() non è annullabile dal renderer: la si corre contro un
    // timer e, se scade, la si abbandona. Il .catch() vuoto evita che il suo
    // rifiuto tardivo diventi una unhandled rejection.
    const request = navigator.bluetooth.requestDevice({
      filters: [{ services: [SERVICE_UUID] }],
    })
    request.catch(() => {})

    // Il countdown ha senso solo su Electron, dove sappiamo se la scansione ha
    // prodotto risultati. Nel browser il picker nativo è opaco: nessun timer, e
    // nessun fallback possibile (manca anche $ipc).
    const racers = [request]

    if (ipc && timeoutMs > 0) {
      racers.push(
        new Promise((resolve, reject) => {
          timer = setTimeout(() => {
            timedOut = true
            // Best effort: in main il callback esiste solo se Chromium ha già
            // scoperto qualcosa, altrimenti è un no-op. Serve a non lasciare lo
            // scan attivo, non a far rifiutare la requestDevice().
            ipc.send('ble:selectDevice', {})
            reject(noDeviceError())
          }, timeoutMs)
        })
      )
    }

    _scanning = true

    try {
      const btDevice = await Promise.race(racers)

      _btDevice = btDevice
      commit('setDevice', { id: btDevice.id, name: btDevice.name })

      // Chromium restituisce sempre la stessa istanza BluetoothDevice per lo
      // stesso dispositivo, quindi senza rimuovere il listener precedente se ne
      // accumulerebbe uno per ogni scan e l'evento verrebbe gestito N volte.
      forgetDisconnectListener()
      _onDisconnected = () => {
        _server = null
        _characteristic = null
        commit('setConnected', false)
      }
      _btDevice.addEventListener('gattserverdisconnected', _onDisconnected)
    } catch (err) {
      // Uno scan precedente abbandonato blocca quello nuovo: anche quel caso
      // vale come "nessun dispositivo", così il click ripiega sul seriale.
      if (timedOut || /already in progress/i.test(err.message || '')) {
        throw noDeviceError()
      }
      throw err
    } finally {
      _scanning = false
      clearTimeout(timer)
      clearTimeout(autoSelectTimer)
      if (ipc) ipc.removeAllListeners('ble:devicesFound')
    }
  },

  // Electron only: forwards the user's device choice to the main process so it
  // can call the select-bluetooth-device callback and resolve requestDevice().
  selectDevice(_, deviceId) {
    const ipc = getIpc()
    if (ipc) ipc.send('ble:selectDevice', { deviceId })
  },

  // Opens the GATT connection and prepares the characteristic for read/write.
  // Must be called after scan() resolves.
  async connect({ commit }) {
    if (!_btDevice) throw new Error('No device selected — call scan() first')
    commit('setError', null)

    _server = await _btDevice.gatt.connect()
    const service = await _server.getPrimaryService(SERVICE_UUID)
    _characteristic = await service.getCharacteristic(CHAR_UUID)
    await _characteristic.startNotifications()

    commit('setConnected', true)
  },

  // eslint-disable-next-line require-await
  async disconnect({ commit }) {
    // Niente da smontare. Senza questa guardia disconnect verrebbe eseguita due
    // volte a ogni flusso — una dal flusso stesso, una dalla chiusura della
    // modale — rimandando un cancel a un picker che non esiste più.
    if (!_scanning && !_btDevice && !_server && !_characteristic) return

    // Cancels a picker still waiting for a pick (no-op otherwise), so closing
    // the modal mid-scan doesn't leave requestDevice() pending forever.
    const ipc = getIpc()
    if (ipc) ipc.send('ble:selectDevice', {})

    if (_server && _server.connected) _server.disconnect()

    // gattserverdisconnected è accodato come task, quindi scatta dopo questa
    // funzione: togliendo il listener adesso l'handler resta riservato ai
    // distacchi imprevisti e non gira su uno stato già resettato.
    forgetDisconnectListener()

    _btDevice = null
    _server = null
    _characteristic = null
    commit('reset')
  },

  // Returns { recognition: 'ok' | 'notOk' | 'greenCard' }
  recognize() {
    return sendCommand(FUNC.recognition)
  },

  // Returns battery level (raw device integer) and stores it in state.
  async battery({ commit }) {
    const value = await sendCommand(FUNC.battery)
    commit('setBattery', value)
    return value
  },

  // Returns { hwMajorVersion, hwMinorVersion, fwMajorVersion, fwMinorVersion }
  async info({ commit }) {
    const data = await sendCommand(FUNC.info)
    commit('setInfo', data)
    return data
  },

  // date: JS Date object
  // Returns { ok: boolean }
  setDate(_, date) {
    return sendCommand(FUNC.setData, dateBytes(date))
  },

  // Returns a JS Date.
  getDate() {
    return sendCommand(FUNC.getData)
  },

  // Total body measurement (bodyRight segment).
  // Returns { status, type, resistance, reactance, phaseAngle, quality } and stores in state.
  async totalMeasure({ commit }) {
    const data = await sendCommand(FUNC.totalMeasureSend, [
      MEASURE_TYPE.bodyRight,
    ])
    commit('setMeasurement', data)
    return data
  },

  // Regional measurement for a specific body segment.
  // type: one of the MEASURE_TYPE keys (e.g. 'armRight', 'legLeft', …)
  async regionalMeasure({ commit }, type) {
    if (!(type in MEASURE_TYPE))
      throw new Error(`Unknown measure type: ${type}`)
    const data = await sendCommand(FUNC.regionalMeasureSend, [
      MEASURE_TYPE[type],
    ])
    commit('setMeasurement', data)
    return data
  },

  // Acknowledge successful data receipt to the device (no parsed response).
  dataReceivedSuccess() {
    return sendCommand(FUNC.dataReceivedFeedback, [0x00], false)
  },

  // Acknowledge failed data receipt to the device (no parsed response).
  dataReceivedError() {
    return sendCommand(FUNC.dataReceivedFeedback, [0x01], false)
  },

  // Returns { ok: boolean }
  deviceAutoOff() {
    return sendCommand(FUNC.deviceAutoOff)
  },

  // Debugger only: writes a prebuilt packet and returns the reply bytes as
  // received, or null when no reply is expected. A 0xfe reply rejects with the
  // bytes on err.bytes.
  async sendRawPacket(_, { packet, reply = true }) {
    if (!reply) {
      await sendPacketNoReply(packet)
      return null
    }

    const { bytes } = await sendPacket(packet)
    return bytes
  },

  // Full regional exam: all ten segments, then acknowledge and power off.
  // Returns { status, result } keyed like the serial regionalBt response body;
  // on a segment the device won't deliver it stops and names it, so the UI can
  // prompt and retry.
  async regionalExam({ commit }) {
    const result = {}

    for (let i = 0; i < BODY_PARTS.length; i++) {
      const data = await sendCommand(FUNC.regionalMeasureSend, [i])
      commit('setMeasurement', data)

      if (data.status !== 'ok') {
        return { status: data.status, segment: BODY_PARTS[i], result }
      }

      if (data.type !== MEASURE_TYPE_BY_VALUE[i])
        throw new Error(
          `Device answered for ${data.type}, expected segment ${i}`
        )

      result[BODY_PARTS[i]] = {
        rz: data.resistance,
        xc: data.reactance,
        pha: data.phaseAngle,
      }
    }

    // The serial flow leaves a second between the acknowledgement and the
    // power-off request; the device needs it to settle.
    await sendCommand(FUNC.dataReceivedFeedback, [0x00], false)
    await delay(1000)
    await sendCommand(FUNC.deviceAutoOff)

    return { status: 'ok', result }
  },
}

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
}
